import { useRef, useState, useEffect } from "react";

import { getShouldShowEmptyEditor } from "@/assignments/utils/assignmentUrlHelpers";
import useClassroomsContext from "@/classrooms/useClassroomsContext";
import getLanguageCode from "@/view/utils/getLanguageCode";
import getTotalAssigneesForPolls from "@/view/utils/getTotalAssigneesForPolls";
import useGetPolls from "@/view/utils/useGetPolls";

const usePollState = ({
  currentAssignment,
  currentLevelLanguage,
  selectedAssignmentId,
  userProperties,
}) => {
  const { classrooms: teacherClassrooms } = useClassroomsContext();
  const totalPollAssignees =
    currentAssignment &&
    getTotalAssigneesForPolls(currentAssignment, teacherClassrooms);
  const isDraftAssignment = currentAssignment?.is_draft;
  const isTeacher = userProperties?.isTeacher;
  const currentLanguage = getLanguageCode(currentLevelLanguage);

  const [activePoll, setActivePoll] = useState();
  const [isPollSelected, setIsPollSelected] = useState(false);
  // Assigned poll refers to a poll that has been created and assigned to an assignment (not a template).
  const [assignedPoll, setAssignedPoll] = useState();
  const pollTemplatesByLanguageRef = useRef({});
  const pollTemplatesByLanguage = pollTemplatesByLanguageRef.current;
  const templatesAlreadyExists = !!pollTemplatesByLanguage[currentLanguage];

  // Editing state variables
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isValidateFormFieldsActive, setIsValidateFormFieldsActive] =
    useState(false);
  const [customPoll, setCustomPoll] = useState({});

  const { data: pollTemplates } = useGetPolls({
    language: currentLanguage,
    shouldFetchTemplates: isTeacher,
    skip: templatesAlreadyExists,
  });

  const { data: assignmentPolls } = useGetPolls({
    // We get the id from currentAssignment as a fallback from selectedAssignmentId because
    // sometimes students will load a page without the selectedAssignmentId in the url param,
    // so we want to get the id from the latest/current assignment if it exists.
    assignmentId: selectedAssignmentId || currentAssignment?.content_id,
    shouldFetchTemplates: false,
  });

  useEffect(() => {
    const assignmentPoll = assignmentPolls?.[assignmentPolls?.length - 1];
    if (assignmentPoll) {
      setAssignedPoll(assignmentPoll);
      setActivePoll(assignmentPoll);
      setIsPollSelected(true);
    }
  }, [assignmentPolls]);

  useEffect(() => {
    // Check if selectedAssignmentId is "null" or maps to an empty assignment
    const shouldShowTemplatePoll =
      getShouldShowEmptyEditor(selectedAssignmentId);

    // Store poll templates by language.
    if (pollTemplates && !templatesAlreadyExists) {
      pollTemplatesByLanguage[currentLanguage] = pollTemplates;
    }

    // Handles any subsequent changes to assignedPoll after initial set.
    if (assignedPoll) {
      setActivePoll(assignedPoll);
    }

    if (pollTemplatesByLanguage[currentLanguage]?.length && !assignedPoll) {
      // Initially sets template based on language.
      setActivePoll(pollTemplatesByLanguage[currentLanguage]?.[3]);
    }

    // If assignment does not contain a poll.
    if (
      selectedAssignmentId &&
      !shouldShowTemplatePoll &&
      assignmentPolls?.length === 0 &&
      !assignedPoll
    ) {
      setIsPollSelected(false);
    }
  }, [assignmentPolls, assignedPoll, pollTemplates]);

  useEffect(() => {
    // Sets templates on any subsequent changes to the article's language.
    if (
      currentLanguage &&
      pollTemplatesByLanguage[currentLanguage]?.length &&
      !assignedPoll
    ) {
      setActivePoll(pollTemplatesByLanguage[currentLanguage]?.[3]);
    }
  }, [currentLanguage]);

  useEffect(() => {
    if (isCreateMode) {
      setIsEditMode(true);
    }
  }, [isCreateMode]);

  useEffect(() => {
    if (!isEditMode) {
      setIsCreateMode(false);
    }
  }, [isEditMode]);

  return {
    activePoll,
    assignedPoll,
    customPoll,
    isCreateMode,
    isDraftAssignment,
    isEditMode,
    isPollSelected,
    isValidateFormFieldsActive,
    pollTemplates: pollTemplatesByLanguage?.[currentLanguage] || [],
    setActivePoll,
    setAssignedPoll,
    setCustomPoll,
    setIsCreateMode,
    setIsEditMode,
    setIsPollSelected,
    setIsValidateFormFieldsActive,
    totalPollAssignees,
  };
};

export default usePollState;
